import { ExpandMore } from "@mui/icons-material";
import { Box, Divider, Menu, MenuItem, Tooltip } from "@mui/material";
import { Fragment, useState } from "react";
import { Button } from "@platform/design-system";

import { RFP_STATUSES, RFPStatus } from "../../../constants/rfp";

import { DateValue } from "./DateValue";

interface Props {
  dueDate: string;
  effectiveDate: string;
  status?: string;
  isStatusChanging: boolean;
  hasFinalDecisions: boolean;
  onStatusChange: (params: { status: string }) => void;
}

function HeaderExtra({
  effectiveDate,
  dueDate,
  status,
  isStatusChanging,
  hasFinalDecisions,
  onStatusChange,
}: Props): React.JSX.Element {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleStatusChange = (newStatus: string): void => {
    onStatusChange({ status: newStatus });
    handleClose();
  };
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        bgcolor: "background.paper",
        borderRadius: 1,
        gap: 2,
        px: 2,
        py: 1,
      }}
    >
      <Box display="flex" px={2} gap={3}>
        <DateValue title="Due Date" date={dueDate} />
        <DateValue title="Effective Date" date={effectiveDate} />
      </Box>
      {status ? (
        <>
          <Divider orientation="vertical" flexItem />
          <Button
            loading={isStatusChanging}
            variant="tertiary"
            onClick={handleClick}
            endIcon={<ExpandMore />}
          >
            {RFP_STATUSES[status]}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            slotProps={{
              list: {
                "aria-labelledby": "status-button",
              },
            }}
          >
            {Object.entries(RFP_STATUSES).map(([value, label]) => {
              const isCompleteMenuItem =
                value === ("complete" satisfies RFPStatus);
              const isCompleteDisabled =
                isCompleteMenuItem && !hasFinalDecisions;

              const menuItem = (
                <MenuItem
                  value={value}
                  selected={value === status}
                  disabled={isCompleteDisabled}
                  onClick={() => handleStatusChange(value)}
                >
                  {label}
                </MenuItem>
              );

              if (isCompleteDisabled) {
                return (
                  <Tooltip
                    key={value}
                    title="Final Decisions must be submitted before marking as Complete"
                    placement="left"
                  >
                    <span>{menuItem}</span>
                  </Tooltip>
                );
              }

              return <Fragment key={value}>{menuItem}</Fragment>;
            })}
          </Menu>
        </>
      ) : null}
    </Box>
  );
}

export { HeaderExtra };
